import React, { useState, useEffect } from 'react';
import AdminSideMenu from '../../components/AdminSideMenu';
import api from "../../utils/axiosInstance";
import { getStateName } from '../../utils/stateSearch';

export default function PendingApprovalPage() {
    const [drivers, setDrivers] = useState([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedAction, setSelectedAction] = useState('');
    const [driverId, setDriverId] = useState(null);
    const [viewDriver, setViewDriver] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 15;
    const pageCount = Math.ceil(drivers.length / itemsPerPage);
    const paginatedDrivers = drivers.slice(
        (currentPage - 1) * itemsPerPage,
        (currentPage - 1) * itemsPerPage + itemsPerPage
    );

    useEffect(() => {
        setCurrentPage(1);
    }, [drivers]);

    useEffect(() => {
        fetchDrivers();
    }, []);

    const fetchDrivers = async () => {
        try {
            const res = await api.get('/pendingdrivers');
            setDrivers(res.data);
        } catch (err) {
            console.error('Error fetching drivers:', err);
        }
    };

    const handleAction = (action, id) => {
        setSelectedAction(action);
        setDriverId(id);
        setModalVisible(true);
    };

    const confirmAction = async () => {
        try {
            if (selectedAction.includes('Approve')) {
                await api.post(`/pendingdrivers/approve/${driverId}`);
                showNotification('Driver approved successfully');
            } else {
                await api.delete(`/pendingdrivers/${driverId}`);
                showNotification('Driver rejected and removed');
            }
            fetchDrivers();
        } catch (err) {
            const errorMsg = err.response?.data?.message || 'Action failed';
            alert(errorMsg);
        }
        finally {
            setModalVisible(false);
        }
    };

    const showNotification = (msg) => {
        const notif = document.createElement('div');
        notif.className =
            'fixed top-4 right-4 bg-white p-4 rounded-lg shadow-lg border-l-4 border-green-500 z-50 flex items-center transform transition-all duration-300';
        notif.innerHTML = `
            <svg class="w-6 h-6 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
            <div>
                <p class="font-semibold">${msg}</p>
                <p class="text-sm text-gray-600">The driver has been processed.</p>
            </div>
        `;
        document.body.appendChild(notif);
        setTimeout(() => notif.remove(), 3000);
    };

    return (
        <div className="flex flex-col md:flex-row min-h-screen bg-gray-50">
            <AdminSideMenu />
            <div className="w-full md:ml-64 p-4 sm:p-6 lg:p-8">
                <header className="pb-4">
                    <h1 className="text-3xl font-bold text-gray-900">Pending Approval</h1>
                    <p className="text-gray-600">Review and approve drivers here</p>
                </header>

                <section className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-8">
                    {drivers.length === 0 ? (
                        <div className="col-span-full text-center text-gray-500 text-lg py-12">
                            No records found
                        </div>
                    ) : (
                        paginatedDrivers.map((driver) => (
                            <div
                                key={driver._id}
                                className="bg-white p-6 rounded-xl border border-gray-200 border-l-4 border-l-truckers-red shadow-sm transition-all duration-300 hover:scale-[1.02] hover:shadow-md relative"
                            >
                                <button
                                    onClick={() => setViewDriver(driver)}
                                    className="absolute top-4 right-4 text-gray-600 hover:text-black"
                                    title="View Details"
                                >
                                    <i className="fas fa-eye text-lg"></i>
                                </button>

                                <div className="flex items-center">
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900">{driver.name}</h3>
                                    </div>
                                </div>
                                <div className="mt-4 text-sm text-gray-600 space-y-1">
                                    <p>
                                        <span className="font-medium">CDL Type:</span> {driver.cdl || 'N/A'}
                                    </p>
                                    <p>
                                        <span className="font-medium">Trailer:</span>{' '}
                                        {driver.trailer && driver.trailer.some(t => t.trim() !== '')
                                            ? driver.trailer.filter(t => t.trim() !== '').join(', ')
                                            : 'N/A'}
                                    </p>

                                    <p>
                                        <span className="font-medium">Phone:</span> {driver.phone || 'N/A'}
                                    </p>
                                    <p>
                                        <span className="font-medium">Experience:</span> {driver.experience || 'N/A'}
                                    </p>
                                    <p>
                                        <span className="font-medium">Address:</span>{' '}
                                        {driver.address || 'N/A'}
                                    </p>
                                    <p>
                                        <span className="font-medium">Date of Register:</span>{' '}
                                        {driver.createdAt ? new Date(driver.createdAt).toLocaleDateString('en-US') : 'N/A'}
                                    </p>
                                </div>

                                <div className="mt-4 flex flex-wrap gap-2">
                                    <button
                                        onClick={() => handleAction('Approve Driver', driver._id)}
                                        className="bg-green-600 text-white px-4 py-2 rounded font-semibold hover:bg-green-700"
                                        style={{ backgroundColor: '#6b7280' }}
                                    >
                                        Approve
                                    </button>
                                    <button
                                        onClick={() => handleAction('Reject Driver', driver._id)}
                                        className="bg-truckers-red text-white px-4 py-2 rounded font-semibold hover:bg-blue-800"
                                        style={{ backgroundColor: '#3c9bf9' }}
                                    >
                                        Reject
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </section>
                {pageCount > 1 && (
                    <div className="flex justify-center mt-10">
                        <nav className="flex items-center space-x-1 bg-white px-4 py-3 rounded-xl shadow-md">
                            {/* Prev button */}
                            <button
                                onClick={() => setCurrentPage(c => Math.max(1, c - 1))}
                                disabled={currentPage === 1}
                                aria-label="Previous page"
                                className={`w-9 h-9 flex items-center justify-center rounded-full transition-all border text-xl
          ${currentPage === 1
                                        ? 'bg-gray-200 text-gray-400 border-gray-200 cursor-not-allowed'
                                        : 'bg-gray-50 text-gray-500 border-gray-300 hover:bg-blue-100 hover:text-blue-700'}
        `}
                            >
                                &lt;
                            </button>
                            {[...Array(pageCount)].map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => setCurrentPage(i + 1)}
                                    aria-current={currentPage === i + 1 ? 'page' : undefined}
                                    className={`w-9 h-9 rounded-full flex items-center justify-center mx-0.5 text-base transition-all font-semibold border
            ${currentPage === i + 1
                                            ? 'bg-blue-600 text-white shadow border-blue-600'
                                            : 'bg-gray-50 text-gray-900 border-gray-300 hover:bg-blue-100 hover:text-blue-700'}
          `}
                                >
                                    {i + 1}
                                </button>
                            ))}
                            {/* Next button */}
                            <button
                                onClick={() => setCurrentPage(c => Math.min(pageCount, c + 1))}
                                disabled={currentPage === pageCount}
                                aria-label="Next page"
                                className={`w-9 h-9 flex items-center justify-center rounded-full transition-all border text-xl
          ${currentPage === pageCount
                                        ? 'bg-gray-200 text-gray-400 border-gray-200 cursor-not-allowed'
                                        : 'bg-gray-50 text-gray-500 border-gray-300 hover:bg-blue-100 hover:text-blue-700'}
        `}
                            >
                                &gt;
                            </button>
                        </nav>
                    </div>
                )}

                {/* Confirm Modal */}
                {modalVisible && (
                    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full text-center">
                            <h3 className="text-xl font-bold text-gray-900 mb-6">{selectedAction}</h3>
                            <div className="flex flex-col sm:flex-row justify-center gap-4">
                                <button
                                    onClick={() => setModalVisible(false)}
                                    className="bg-gray-300 text-gray-900 px-5 py-2 rounded hover:bg-gray-400"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmAction}
                                    className="text-white px-5 py-2 rounded hover:bg-blue-800"
                                    style={{ backgroundColor: '#3c9bf9' }}
                                >
                                    OK
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* View Driver Modal */}
                {viewDriver && (
                    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl shadow-lg p-6 max-w-2xl w-full relative">
                            <button
                                className="absolute top-4 right-4 text-black text-6xl p-1"
                                onClick={() => setViewDriver(null)}
                            >
                                &times;
                            </button>
                            <h2 className="text-2xl font-bold mb-4">Driver Details</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
                                <p><strong>Name:</strong> {viewDriver.name}</p>
                                <p><strong>Email:</strong> {viewDriver.email}</p>
                                <p><strong>Experience:</strong> {viewDriver.experience || 'N/A'}</p>
                                <p><strong>Route:</strong> {Array.isArray(viewDriver.route) && viewDriver.route.some(r => r && r.trim() !== '') ? viewDriver.route.filter(r => r && r.trim() !== '').join(', ') : 'N/A'}</p>
                                <p><strong>Job Type:</strong> {viewDriver.job || 'N/A'}</p>
                                <p><strong>Trailer:</strong> {Array.isArray(viewDriver.trailer) && viewDriver.trailer.some(t => t && t.trim() !== '') ? viewDriver.trailer.filter(t => t && t.trim() !== '').join(', ') : 'N/A'}</p>
                                <p><strong>CDL:</strong> {viewDriver.cdl || 'N/A'}</p>
                                <p><strong>Endorsements:</strong> {Array.isArray(viewDriver.endorsements) && viewDriver.endorsements.some(e => e && e.trim() !== '') ? viewDriver.endorsements.filter(e => e && e.trim() !== '').join(', ') : 'N/A'}</p>                                <p><strong>Phone:</strong> {viewDriver.phone || 'N/A'}</p>
                                <p><strong>Address:</strong> {viewDriver.address || 'N/A'}</p>
                                <p><strong>City:</strong> {viewDriver.city || 'N/A'}</p>
                                <p><strong>State:</strong> {viewDriver.state ? `${getStateName(viewDriver.state)} (${viewDriver.state})` : 'N/A'}</p>                                <p><strong>ZIP:</strong> {viewDriver.zip}</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
